import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import JSZip from 'jszip';
import { ensureFIGVault } from '../../api/figApi';
import { buildVaultPath, statusToDocType } from '../../utils/buildVaultPath';

const GRAPH = 'https://graph.microsoft.com/v1.0';

// Filename sanitizer: non-alphanumeric → _, trim underscores.
const sanitize = (s) => String(s || '').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function triggerBrowserDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function encodePath(p) {
  return p.split('/').map(encodeURIComponent).join('/');
}

// LOB row with indeterminate checkbox support.
function LobCheckbox({ state, onChange, label, count }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'indeterminate';
  }, [state]);
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
      padding: '0.4rem 0.5rem', background: '#f4eedb', borderRadius: '4px', userSelect: 'none' }}>
      <input ref={ref} type="checkbox"
        checked={state === 'all'}
        onChange={e => onChange(e.target.checked)} />
      <span style={{ fontWeight: 700, color: '#4B0082', fontSize: '0.85rem' }}>{label}</span>
      <span style={{ fontSize: '0.7rem', color: '#888' }}>({count})</span>
    </label>
  );
}

// --- Component ---

const STATUS_RANK = { Active: 0, Bound: 1, Quote: 2, Cancelled: 3, Terminated: 4 };

export default function FIGFolioExport({ insured, policies, getToken, onClose }) {
  // Pre-filter to Active/Bound only.
  const activePolicies = useMemo(
    () => (policies || []).filter(p => p.Status === 'Active' || p.Status === 'Bound'),
    [policies]
  );

  const [selected, setSelected] = useState(() => new Set(activePolicies.map(p => p.id)));
  const [phase, setPhase] = useState('select');
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [log, setLog] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const abortRef = useRef(null);

  // Group policies by LOB.
  const lobGroups = useMemo(() => {
    const sorted = [...activePolicies].sort((a, b) => {
      const sa = STATUS_RANK[a.Status] ?? 9;
      const sb = STATUS_RANK[b.Status] ?? 9;
      if (sa !== sb) return sa - sb;
      const da = Date.parse(a.EffectiveDate) || 0;
      const db = Date.parse(b.EffectiveDate) || 0;
      return db - da;
    });
    const map = new Map();
    for (const p of sorted) {
      const label = (p.LOB || '').trim() || 'Unclassified';
      const key = `lob-${label.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
      if (!map.has(key)) map.set(key, { key, label, policies: [], bestRank: 9 });
      const g = map.get(key);
      g.policies.push(p);
      const r = STATUS_RANK[p.Status] ?? 9;
      if (r < g.bestRank) g.bestRank = r;
    }
    return [...map.values()].sort((a, b) =>
      a.bestRank - b.bestRank || a.label.localeCompare(b.label)
    );
  }, [activePolicies]);

  function lobState(group) {
    const checked = group.policies.filter(p => selected.has(p.id)).length;
    if (checked === 0) return 'none';
    if (checked === group.policies.length) return 'all';
    return 'indeterminate';
  }

  function toggleLob(group, on) {
    setSelected(prev => {
      const next = new Set(prev);
      for (const p of group.policies) { if (on) next.add(p.id); else next.delete(p.id); }
      return next;
    });
  }

  function togglePolicy(policyId) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(policyId)) next.delete(policyId); else next.add(policyId);
      return next;
    });
  }

  const selectAll = () => setSelected(new Set(activePolicies.map(p => p.id)));
  const deselectAll = () => setSelected(new Set());

  const handleClose = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    onClose();
  }, [onClose]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') handleClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose]);

  // --- Export pipeline (vault-only, no email search) ---
  async function runExport() {
    const selectedPolicies = activePolicies.filter(p => selected.has(p.id));
    if (selectedPolicies.length === 0) return;
    setPhase('exporting');
    setProgress({ completed: 0, total: selectedPolicies.length });
    setLog([]);
    setErrorMsg('');

    const abort = new AbortController();
    abortRef.current = abort;
    let completed = 0;

    try {
      const token = await getToken();
      const driveId = await ensureFIGVault(token);

      // Group selected policies by LOB so we list each vault folder once.
      const byLob = new Map();
      for (const p of selectedPolicies) {
        const lob = (p.LOB || '').trim() || 'Unclassified';
        if (!byLob.has(lob)) byLob.set(lob, []);
        byLob.get(lob).push(p);
      }

      // For each LOB, list the vault folder once, then match all policies in that LOB.
      // Folder path resolves through buildVaultPath — spec-authoritative per FIGVault_Spec_v1.6.
      const hits = []; // [{ policy, fileId, fileName }]
      for (const [lob, lobPolicies] of byLob) {
        if (abort.signal.aborted) break;

        // Probe folder path once per LOB (all policies in this LOB share it).
        // Folder path is returned even when filename-level fields (EffectiveDate) are missing.
        const probe = buildVaultPath({ insured, policy: lobPolicies[0], docType: 'Policy' });
        const folderPath = probe.folderPath;

        if (!folderPath) {
          console.warn('[Folio] Could not build folder path for LOB:', lob, probe.errors);
          for (const policy of lobPolicies) {
            setLog(prev => [...prev, {
              note: `— ${policy.Carrier || 'Unknown'} ${policy.PolicyNumber || '—'}: path unresolvable`,
            }]);
            completed++;
            setProgress({ completed, total: selectedPolicies.length });
          }
          continue;
        }

        let folderFiles = [];
        try {
          const res = await fetch(
            `${GRAPH}/drives/${driveId}/root:/${encodePath(folderPath)}:/children`
            + `?$select=name,id,file,size&$top=200`,
            { headers: { Authorization: `Bearer ${token}` }, signal: abort.signal }
          );
          if (res.ok) {
            const data = await res.json();
            folderFiles = (data.value || []).filter(it => it.file && /\.pdf$/i.test(it.name));
          } else {
            console.warn(`[Folio] Vault folder not found: ${folderPath} (${res.status})`);
          }
        } catch (e) {
          if (e.name === 'AbortError') throw e;
          console.warn(`[Folio] Error listing ${folderPath}:`, e.message);
        }

        // Match each policy to a file. Primary: exact filename via helper.
        // Loose fallback: same carrier+policyNumber+DocType (v1.3 Rule 9 — multiple
        // terms coexist in the folder, so DocType is required in the filter).
        // Only when vp.carrierShort is resolvable; if carrier is unresolvable,
        // never substring-scan — sibling records with the same policy number
        // (e.g. Quoted renewal vs Active current term) would wrongly match.
        for (const policy of lobPolicies) {
          if (abort.signal.aborted) break;
          const docType = statusToDocType(policy.Status);
          const vp = buildVaultPath({ insured, policy, docType, effectiveDate: policy.EffectiveDate });
          let match = null;
          let noCarrier = false;

          if (vp.filename && vp.carrierShort) {
            const expected = vp.filename.toLowerCase();
            match = folderFiles.find(f => (f.name || '').toLowerCase() === expected);
            if (!match) {
              const stripped = (vp.filename.split('_')[1] || '').toLowerCase();
              const carrierPrefix = `${vp.carrierShort}_`.toLowerCase();
              const docTypeFragment = `_${docType}_`.toLowerCase();
              if (stripped) {
                match = folderFiles.find(f => {
                  const n = (f.name || '').toLowerCase();
                  return n.startsWith(carrierPrefix)
                    && n.includes(stripped)
                    && n.includes(docTypeFragment);
                });
              }
            }
          } else {
            noCarrier = vp.errors.some(e => e.code === 'NO_CARRIER' || e.code === 'CARRIER_UNRESOLVABLE');
          }

          if (match) {
            hits.push({ policy, fileId: match.id, fileName: match.name });
            setLog(prev => [...prev, {
              note: `✓ ${policy.Carrier || 'Unknown'} ${policy.PolicyNumber || '—'}: ${match.name}`,
            }]);
          } else {
            const reason = noCarrier ? 'no carrier on record' : 'not found in vault';
            console.warn(`[Folio] No PDF for ${policy.Carrier || '—'} ${policy.PolicyNumber || '—'}: ${reason}`);
            setLog(prev => [...prev, {
              note: `— ${policy.Carrier || 'Unknown'} ${policy.PolicyNumber || '—'}: ${reason}`,
            }]);
          }
          completed++;
          setProgress({ completed, total: selectedPolicies.length });
        }
      }

      if (hits.length === 0) {
        setErrorMsg('No policy PDFs found in the vault for the selected policies.');
        setPhase('error');
        return;
      }

      // Download each matched PDF and add to ZIP.
      setLog(prev => [...prev, { note: `\nDownloading ${hits.length} file${hits.length === 1 ? '' : 's'}...` }]);
      const zip = new JSZip();
      for (const { policy, fileId, fileName } of hits) {
        if (abort.signal.aborted) break;
        const res = await fetch(`${GRAPH}/drives/${driveId}/items/${fileId}/content`, {
          headers: { Authorization: `Bearer ${token}` }, signal: abort.signal,
        });
        if (!res.ok) {
          console.warn(`[Folio] Download failed for ${fileName} (${res.status})`);
          continue;
        }
        const bytes = await res.arrayBuffer();
        const lobLabel = ((policy.LOB || '').trim() || 'Unclassified').replace(/\//g, '-');
        const carrier = sanitize(policy.Carrier) || 'Unknown';
        const policyNum = sanitize(policy.PolicyNumber) || String(policy.id);
        zip.folder(lobLabel).file(`${carrier}_${policyNum}.pdf`, bytes);
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      const companyKey = String(insured.CompanyName || 'Insured').replace(/\s+/g, '');
      const zipName = `${companyKey}_Policy_Folio_${todayISO()}.zip`;
      triggerBrowserDownload(blob, zipName);

      setPhase('done');
    } catch (e) {
      if (e.name === 'AbortError') { setPhase('select'); return; }
      console.error('[Folio] Export failed:', e);
      setErrorMsg(e.message || String(e));
      setPhase('error');
    } finally {
      abortRef.current = null;
    }
  }

  // --- Render ---
  const selectedCount = selected.size;
  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  };
  const modal = {
    background: '#fffdf5', borderRadius: '8px', width: 'min(640px, 92vw)',
    maxHeight: '88vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)', border: '1px solid #d8d1bf',
  };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
      <div style={modal}>
        {/* Header */}
        <div style={{ padding: '0.9rem 1rem', borderBottom: '1px solid #e5dfd0',
          display: 'flex', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: '#4B0082', fontSize: '1rem' }}>
              Export Policy Folio
            </div>
            <div style={{ fontSize: '0.75rem', color: '#777', marginTop: '2px' }}>
              {insured.CompanyName} — Active / Bound policies only
            </div>
          </div>
          <button onClick={handleClose}
            style={{ background: 'none', border: 'none', fontSize: '1.3rem',
              cursor: 'pointer', color: '#888', padding: '0 4px' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 1rem' }}>
          {phase === 'select' && (
            <>
              {lobGroups.length === 0 && (
                <p style={{ textAlign: 'center', color: '#888', padding: '2rem 0' }}>
                  No active or bound policies to export.
                </p>
              )}
              {lobGroups.length > 0 && (
                <>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '0.6rem' }}>
                    <button onClick={selectAll} className="FIG-back-btn" style={{ fontSize: '0.75rem' }}>
                      Select All
                    </button>
                    <button onClick={deselectAll} className="FIG-back-btn" style={{ fontSize: '0.75rem' }}>
                      Deselect All
                    </button>
                  </div>
                  {lobGroups.map(group => (
                    <div key={group.key} style={{ marginBottom: '0.6rem' }}>
                      <LobCheckbox
                        state={lobState(group)}
                        onChange={(on) => toggleLob(group, on)}
                        label={group.label}
                        count={group.policies.length}
                      />
                      <div style={{ marginLeft: '1.5rem', marginTop: '0.3rem' }}>
                        {group.policies.map(p => (
                          <label key={p.id} style={{ display: 'flex', alignItems: 'center',
                            gap: '8px', padding: '0.3rem 0.4rem', fontSize: '0.8rem',
                            cursor: 'pointer', borderBottom: '1px solid #f0ebe0' }}>
                            <input type="checkbox" checked={selected.has(p.id)}
                              onChange={() => togglePolicy(p.id)} />
                            <span style={{ flex: 1, display: 'flex', gap: '6px',
                              flexWrap: 'wrap', alignItems: 'center' }}>
                              <span style={{ fontWeight: 600, color: '#333' }}>
                                {p.Carrier || '—'}
                              </span>
                              <span style={{ color: '#666' }}>{p.PolicyNumber || '(no #)'}</span>
                              <span style={{ color: '#888', fontSize: '0.72rem' }}>
                                {p.EffectiveDate || ''}{p.ExpirationDate ? ` → ${p.ExpirationDate}` : ''}
                              </span>
                              {p.Premium && (
                                <span style={{ color: '#666', fontSize: '0.72rem' }}>
                                  ${Number(p.Premium).toLocaleString()}
                                </span>
                              )}
                              {p.Status && (
                                <span className={`FIG-card-status FIG-card-status--${String(p.Status).toLowerCase()}`}
                                  style={{ fontSize: '0.65rem' }}>{p.Status}</span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}

          {phase === 'exporting' && (
            <div>
              <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#4B0082', fontWeight: 600 }}>
                {progress.completed} of {progress.total} policies checked...
              </div>
              <div style={{ height: '10px', background: '#eee7d4', borderRadius: '5px', overflow: 'hidden' }}>
                <div style={{
                  width: `${progress.total ? (progress.completed / progress.total) * 100 : 0}%`,
                  height: '100%', background: '#4B0082', transition: 'width 0.2s',
                }} />
              </div>
              <div style={{ marginTop: '0.6rem', maxHeight: '200px', overflowY: 'auto',
                fontSize: '0.72rem', color: '#666', fontFamily: 'monospace' }}>
                {log.map((l, i) => <div key={i}>{l.note}</div>)}
              </div>
            </div>
          )}

          {phase === 'done' && (
            <div>
              <div style={{ color: '#1e7a3a', fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.5rem' }}>
                ✓ Folio exported
              </div>
              <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.5rem' }}>
                {log.filter(l => l.note.startsWith('✓')).length} of {progress.total} policies included.
              </div>
              <div style={{ maxHeight: '260px', overflowY: 'auto', fontSize: '0.72rem',
                color: '#666', fontFamily: 'monospace', background: '#faf7ee',
                padding: '0.5rem', borderRadius: '4px' }}>
                {log.map((l, i) => <div key={i}>{l.note}</div>)}
              </div>
            </div>
          )}

          {phase === 'error' && (
            <div>
              <div style={{ color: '#a12b2b', fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.5rem' }}>
                Export failed
              </div>
              <div style={{ fontSize: '0.8rem', color: '#555' }}>{errorMsg}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #e5dfd0',
          display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          {phase === 'select' && (
            <>
              <button onClick={handleClose} className="FIG-back-btn">Cancel</button>
              <button onClick={runExport} className="FIG-save-btn" disabled={selectedCount === 0}>
                &#8615; Export Folio ({selectedCount})
              </button>
            </>
          )}
          {phase === 'exporting' && (
            <button onClick={() => abortRef.current?.abort()} className="FIG-back-btn">
              Cancel Export
            </button>
          )}
          {(phase === 'done' || phase === 'error') && (
            <button onClick={handleClose} className="FIG-save-btn">Close</button>
          )}
        </div>
      </div>
    </div>
  );
}
